import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Card,
    Col,
    Table,
    Button,
    Select,
    DatePicker,
    Typography,
    Dropdown,
    Input,
} from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import SpeciesBirdIdentificationMap from '../map/SpeciesBirdIdentificationMap';
import { useKeycloak } from '@react-keycloak/web';
import { fetchEviaDetections, fetchThraceDetections } from '../../externalAPI/clickhouse';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
const { Option } = Select;
const { RangePicker } = DatePicker;

// Define interfaces for data structures
export interface BirdData {
    key: number;
    sensorId: string;
    timestamp: string;
    commonName: string;
    scientificName: string;
    identificationProbability: number;
    conservationStatus?: string;
    protectionStatus?: string[];
    controlOrStudyarea?: string;
}

export interface SensorStats {
    sensorId: string;
    totalRecordings: number;
    uniqueSpecies: number;
    avgProbability: number;
    lastRecording: string;
    recordings: BirdData[];
}

function useDebounce(value: any, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}


const SpeciesBirdGreekSpatialAnalysis: React.FC = () => {
    // State management for UI interactions and data
    const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
    const [hoveredSensorId, setHoveredSensorId] = useState<string | null>(null);
    const [monitoringData, setMonitoringData] = useState<BirdData[]>([]);
    const [loading, setLoading] = useState(true);
    const [greeceLocation, setGreeceLocation] = useState<string | null>(null);
    const [eviaData, setEviaData] = useState<BirdData[]>([]);
    const [thraceData, setThraceData] = useState<BirdData[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [chartDataKey, setChartDataKey] = useState<'count' | 'uniqueSpecies'>('uniqueSpecies');
    const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]); // Change from string | undefined
    // New state for sensor ID filter
    const [sensorIdFilter, setSensorIdFilter] = useState<string>('');
    const debouncedSensorIdFilter = useDebounce(sensorIdFilter, 300);

    // Filter states - only control/studyarea and dateRange remain
    const [selectedControlOrStudyArea, setSelectedControlOrStudyArea] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<any>(null);
    // Keycloak integration for user case study
    const { keycloak } = useKeycloak();
    const userCaseStudy = keycloak.idTokenParsed?.case_study as string | undefined;
    const chartRef = useRef(null);

    // Effect to load initial data based on user's case study
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true); // Set loading to true before fetching data
            try {
                if (userCaseStudy === 'Greece') {
                    if (greeceLocation === 'Evia') {
                        // Await the fetch function directly and then set the state
                        const eviaData = await fetchEviaDetections();
                        setEviaData(eviaData);
                    } else if (greeceLocation === 'Thrace') {
                        // Await the fetch function directly and then set the state
                        const thraceData = await fetchThraceDetections();
                        setThraceData(thraceData);
                    }
                }
            } catch (error) {
                console.error('Error loading bird data:', error);
                // You might want to set an error state here as well
            } finally {
                setLoading(false); // Set loading to false after data is fetched or an error occurs
            }
        };
        loadInitialData();
    }, [userCaseStudy, greeceLocation]);

    // Effect to set monitoring data based on selected Greece location
    useEffect(() => {
        if (userCaseStudy === 'Greece') {
            if (greeceLocation === 'Evia') {
                setMonitoringData(eviaData);
            } else if (greeceLocation === 'Thrace') {
                setMonitoringData(thraceData);
            } else {
                // If no specific location is selected, combine or default to empty
                setMonitoringData([]);
            }
        } else {
            // If not Greece case study, clear monitoring data
            setMonitoringData([]);
        }
    }, [greeceLocation, eviaData, thraceData, userCaseStudy]);

    const uniqueSpecies = useMemo(() => {
        const allSpecies = monitoringData.map(item =>
            item.commonName && item.commonName.trim() !== '' ? item.commonName : item.scientificName
        );
        return [...new Set(allSpecies)].filter(Boolean).sort();
    }, [monitoringData]);

    // Memoized filtered data based on selected filters including sensor ID filter
    const filteredData = useMemo(() => {
        let filtered = [...monitoringData];

        // Sensor ID Filter - Using debounced value
        if (debouncedSensorIdFilter) {
            filtered = filtered.filter(
                (item) => item.sensorId.toLowerCase().includes(debouncedSensorIdFilter.toLowerCase())
            );
        }

        // Control/Study area filter
        if (selectedControlOrStudyArea) {
            filtered = filtered.filter(
                (item) => item.controlOrStudyarea === selectedControlOrStudyArea
            );
        }

        // Species name filter
        if (selectedSpecies && selectedSpecies.length > 0) {
            filtered = filtered.filter(item => {
                const speciesName = item.commonName && item.commonName.trim() !== ''
                    ? item.commonName
                    : item.scientificName;
                return selectedSpecies.includes(speciesName);
            });
        }

        // Date range filter
        if (dateRange && dateRange.length === 2) {
            const [startDate, endDate] = dateRange;
            filtered = filtered.filter((item) => {
                const itemDate = new Date(item.timestamp);
                return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
            });
        }

        return filtered;
    }, [monitoringData, selectedControlOrStudyArea, dateRange, selectedSpecies, debouncedSensorIdFilter]);

    // Memoized sensor statistics calculation
    const sensorStats = useMemo((): Record<string, SensorStats> => {
        const stats: Record<string, SensorStats> = {};
        filteredData.forEach((item) => {
            if (!stats[item.sensorId]) {
                stats[item.sensorId] = {
                    sensorId: item.sensorId,
                    totalRecordings: 0,
                    uniqueSpecies: 0,
                    avgProbability: 0,
                    lastRecording: '',
                    recordings: [],
                };
            }
            stats[item.sensorId].recordings.push(item);
        });

        // Calculate derived statistics for each sensor
        Object.values(stats).forEach((stat) => {
            stat.totalRecordings = stat.recordings.length;
            stat.uniqueSpecies = new Set(stat.recordings.map((r) => r.commonName)).size;
            stat.avgProbability = Math.round(
                stat.recordings.reduce((sum, r) => sum + r.identificationProbability, 0) / stat.totalRecordings,
            );
            stat.lastRecording =
                stat.recordings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                    ?.timestamp || '';
        });

        return stats;
    }, [filteredData]);

    // Get unique values for the control/study area filter
    const uniqueControlOrStudyAreas = useMemo(
        () => [...new Set(monitoringData.map((item) => item.controlOrStudyarea))].filter(Boolean).sort() as string[],
        [monitoringData],
    );

    // Event handlers for map and table interactions
    const handleSensorSelect = useCallback((sensorId: string | null) => {
        setSelectedSensorId(sensorId);
    }, []);

    const handleSensorHover = useCallback((sensorId: string | null) => {
        setHoveredSensorId(sensorId);
    }, []);

    const handleRowClick = useCallback(
        (record: BirdData) => {
            const newSelection = selectedSensorId === record.sensorId ? null : record.sensorId;
            setSelectedSensorId(newSelection);
        },
        [selectedSensorId],
    );

    // Function to clear all active filters
    const clearFilters = useCallback(() => {
        setSelectedControlOrStudyArea(undefined);
        setDateRange(null);
        setSelectedSensorId('');
        setSelectedSpecies([]);
    }, []);

    const handleChartDataChange = useCallback((value: 'count' | 'uniqueSpecies') => {
        setChartDataKey(value);
    }, []);

    const micChartData = useMemo(() => {
        if (chartDataKey === 'count') {
            const micSpeciesSets: { [key: string]: Set<string> } = {}; // To store unique species for each sensorId
            const micAreas: { [key: string]: string } = {}; // To store the area for each sensor

            filteredData.forEach((item) => {
                const speciesName = item.commonName && item.commonName.trim() !== ''
                    ? item.commonName
                    : item.scientificName;

                if (!micSpeciesSets[item.sensorId]) {
                    micSpeciesSets[item.sensorId] = new Set<string>();
                }
                micSpeciesSets[item.sensorId].add(speciesName);

                if (item.controlOrStudyarea) {
                    micAreas[item.sensorId] = item.controlOrStudyarea;
                }
            });

            return Object.entries(micSpeciesSets)
                .map(([sensorId, speciesSet]) => ({
                    sensorId,
                    numberOfSpecies: speciesSet.size, // This is the number of different species
                    controlOrStudyarea: micAreas[sensorId] // Attach the area to the data point
                }))
                .sort((a, b) => b.numberOfSpecies - a.numberOfSpecies);
        } else {
            // For "Number of Species" - stacked bar chart with mic vs species detections
            const micSpeciesData: { [key: string]: { [key: string]: number } } = {};

            filteredData.forEach((item) => {
                const speciesName = item.commonName && item.commonName.trim() !== ''
                    ? item.commonName
                    : item.scientificName;

                if (!micSpeciesData[item.sensorId]) {
                    micSpeciesData[item.sensorId] = {};
                }
                micSpeciesData[item.sensorId][speciesName] = (micSpeciesData[item.sensorId][speciesName] || 0) + 1;
            });

            // Convert to format suitable for stacked bar chart
            return Object.entries(micSpeciesData).map(([sensorId, speciesData]) => ({
                sensorId,
                ...speciesData,
                total: Object.values(speciesData).reduce((sum, count) => sum + count, 0)
            })).sort((a, b) => b.total - a.total);
        }
    }, [filteredData, chartDataKey]);

    // Table columns configuration
    const columns = [
        {
            title: 'Sensor ID',
            dataIndex: 'sensorId',
            key: 'sensorId',
            sorter: (a: BirdData, b: BirdData) => a.sensorId.localeCompare(b.sensorId),
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            sorter: (a: BirdData, b: BirdData) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
            render: (text: string) => {
                const date = new Date(text);
                if (isNaN(date.getTime())) {
                    return 'Invalid Date';
                }
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'long' });
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
            },
        },
        {
            title: 'Common Name',
            dataIndex: 'commonName',
            key: 'commonName',
            sorter: (a: BirdData, b: BirdData) => (a.commonName ?? '').localeCompare(b.commonName ?? ''),
        },
        {
            title: 'Scientific Name',
            dataIndex: 'scientificName',
            key: 'scientificName',
            sorter: (a: BirdData, b: BirdData) => a.scientificName.localeCompare(b.scientificName),
        },
        {
            title: 'Probability',
            dataIndex: 'identificationProbability',
            key: 'identificationProbability',
            sorter: (a: BirdData, b: BirdData) => a.identificationProbability - b.identificationProbability,
            render: (probability: number) => `${probability.toFixed(2)}%`,
        },
        {
            title: 'Control/Study Area',
            dataIndex: 'controlOrStudyarea',
            key: 'controlOrStudyarea',
            sorter: (a: BirdData, b: BirdData) => (a.controlOrStudyarea ?? '').localeCompare(b.controlOrStudyarea ?? ''),
            render: (area: string | undefined) => area || 'N/A',
        },
    ];

    // Handlers for the Greece location dropdown
    const handleGreeceLocationChange = useCallback((value: string | null) => {
        setGreeceLocation(value);
    }, []);

    const locations = [
        {
            key: 'Evia',
            label: 'Evia',
        },
        {
            key: 'Thrace',
            label: 'Thrace',
        },
    ];

    const handleMenuClick = (e: { key: string }) => {
        handleGreeceLocationChange(e.key);
    };

    const menuProps = {
        items: locations,
        onClick: handleMenuClick,
    };

    const speciesColors = useMemo(() => {
        const species = new Set<string>();
        filteredData.forEach((item) => {
            const speciesName = item.commonName && item.commonName.trim() !== ''
                ? item.commonName
                : item.scientificName;
            species.add(speciesName);
        });

        const colors = [
            '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
            '#0088fe', '#00c49f', '#ffbb28', '#ff8042', '#8dd1e1',
            '#d084d0', '#87ceeb', '#ffd700', '#ff1493', '#9acd32',
            '#dda0dd', '#98fb98', '#f0e68c', '#ff1493', '#00ced1'
        ];

        const speciesColorMap: { [key: string]: string } = {};
        Array.from(species).forEach((speciesName, index) => {
            speciesColorMap[speciesName] = colors[index % colors.length];
        });

        return speciesColorMap;
    }, [filteredData]);

    const areaColors = useMemo(() => {
        const areas = new Set<string>();
        filteredData.forEach(item => {
            if (item.controlOrStudyarea) {
                areas.add(item.controlOrStudyarea);
            }
        });

        const colors = [
            '#4a90e2', // Color for 'Control_area'
            '#f5a623'  // Color for 'Study_area'
        ];

        const areaColorMap: { [key: string]: string } = {};
        Array.from(areas).forEach((area, index) => {
            areaColorMap[area] = colors[index % colors.length];
        });

        return areaColorMap;
    }, [filteredData]);

    const exportToCsv = () => {
        // Get the headers from the columns array
        const headers = columns.map(col => col.title);
        // Map the filtered data to an array of arrays, with each inner array representing a row
        const rows = filteredData.map(record => columns.map(col => (record as any)[col.dataIndex]));
        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');
        // Create a temporary link element to trigger the download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `detection_records_${new Date().toISOString().slice(0, 19).replace(/T|:/g, '-')}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadChart = async () => {
        if (!chartRef.current) {
            console.error('Chart container not found');
            return;
        }

        try {
            // Capture the chart as a canvas
            const canvas = await html2canvas(chartRef.current, {
                backgroundColor: null,
                scale: 2, // For higher resolution
                useCORS: true,
                logging: false,
                ignoreElements: (element) => {
                    // Ignore any elements that shouldn't be in the export
                    return element.classList.contains('ignore-export');
                }
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    // Save the blob as a PNG file
                    saveAs(blob, `bird_detection_chart_${new Date().toISOString().slice(0, 19)}.png`);
                }
            }, 'image/png');
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    return (
        <div style={{ minHeight: '100vh' }}>
            <h1 style={{ color: '#065f46', fontSize: '1.5rem', fontWeight: '600' }}>
                Home &gt; Species &gt; Spatial Analysis
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginLeft: '-8px', marginRight: '-8px' }}>
                {/* "Select a location" component */}
                {userCaseStudy === 'Greece' && (
                    <Col xs={24} sm={12} md={8} lg={6} style={{ paddingLeft: '8px', paddingRight: '8px', marginTop: '1rem', paddingBottom: '0.5rem' }}>
                        <Card style={{ borderRadius: '0.5rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <Typography.Text strong style={{ color: '#374151' }}>Select a location:</Typography.Text>
                                <Dropdown menu={menuProps}>
                                    <Button
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            borderRadius: '0.375rem',
                                            border: '1px solid #d1d5db',
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#ffffff',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                        onFocus={(e) => {
                                            e.currentTarget.style.outline = '2px solid transparent';
                                            e.currentTarget.style.outlineOffset = '2px';
                                            e.currentTarget.style.boxShadow = '0 0 0 2px #ffffff, 0 0 0 4px #22c55e';
                                        }}
                                        onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                        {greeceLocation || '--- Select ---'}
                                        <DownOutlined id='SelectAreaGreeceBirdsId' style={{ marginLeft: '0.5rem' }} />
                                    </Button>
                                </Dropdown>
                            </div>
                        </Card>
                    </Col>
                )}

                {/* Map Section */}
                <div style={{ paddingLeft: '8px', paddingRight: '8px', flexBasis: '100%', maxWidth: '100%' }}>
                    <div style={{ marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                    <span>Sensor Network Map</span>
                                </div>
                            }
                            style={{ marginBottom: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)' }}
                        >
                            <SpeciesBirdIdentificationMap
                                sensorStats={sensorStats}
                                selectedSensorId={selectedSensorId}
                                hoveredSensorId={hoveredSensorId}
                                onSensorSelect={handleSensorSelect}
                                onSensorHover={handleSensorHover}
                                location={greeceLocation}
                            />
                            <Typography.Paragraph
                                style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}
                            >
                                Installed wind-turbines data were sourced from the publicly accessible geoportal of the Hellenic Regulatory Authority for Waste, Energy and Water (<a href="https://geo.rae.gr/" target="_blank" rel="noopener noreferrer">https://geo.rae.gr/</a>). Accessed Feb. 2025.

                            </Typography.Paragraph>
                            <Typography.Paragraph style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Learn more about AvesEcho:<Typography.Link href='https://guarden.org/technologies/avesecho/' target='_blank'> https://guarden.org/technologies/avesecho/</Typography.Link>
                            </Typography.Paragraph>
                        </Card>
                    </div>
                </div>

                {/* Data Table Section */}
                <div style={{ paddingLeft: '8px', paddingRight: '8px', marginBottom: '1rem', flexBasis: '100%', maxWidth: '100%' }}>
                    <div style={{ marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                <span>Detection Records </span>
                                <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#4b5563' }}>
                                    {filteredData.length} of {monitoringData.length} records
                                    {selectedSensorId && ` (Sensor: ${selectedSensorId})`}
                                </span>
                            </div>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            {/* Filters Section - UPDATED WITH SENSOR ID FILTER */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '1rem',
                                paddingBottom: '1rem',
                            }}>
                                {/* Sensor ID Filter */}
                                <div style={{ flex: '1 1 250px', minWidth: '250px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <Typography.Text strong style={{ color: '#374151', fontSize: '0.875rem' }}>Sensor ID</Typography.Text>
                                        <Input
                                            id='SensorIdFilterBirdsId'
                                            placeholder='Search by Sensor ID...'
                                            value={sensorIdFilter}
                                            onChange={(e) => setSensorIdFilter(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                {/* Control/Study Area Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <Typography.Text strong style={{ color: '#374151', fontSize: '0.875rem' }}>Control/Study Area</Typography.Text>
                                        <Select
                                            id='ControlOrStudyAreaBirdsId'
                                            placeholder='Select an Area'
                                            value={selectedControlOrStudyArea}
                                            onChange={setSelectedControlOrStudyArea}
                                            style={{ width: '100%' }}
                                            allowClear
                                        >
                                            {uniqueControlOrStudyAreas.map((area) => (
                                                <Option key={area} value={area}>{area}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                {/* Species Name Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <Typography.Text strong style={{ color: '#374151', fontSize: '0.875rem' }}>Species Name</Typography.Text>
                                        <Select
                                            id='SpeciesNameBirdsId'
                                            mode="multiple"
                                            placeholder='Select one or more Species'
                                            value={selectedSpecies}
                                            onChange={setSelectedSpecies}
                                            style={{ width: '100%' }}
                                            allowClear
                                        >
                                            {uniqueSpecies.map(species => (
                                                <Option key={species} value={species}>{species}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                {/* Date Range Filter */}
                                <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Date Range</span>
                                        <RangePicker
                                            id="FilterDateBirdsId"
                                            value={dateRange}
                                            onChange={setDateRange}
                                            showTime
                                            format='YYYY-MM-DD HH:mm'
                                            style={{ width: '100%' }}
                                            disabledDate={(current) => {
                                                // Define min/max dates (months are 0-indexed)
                                                const minDate = dayjs(new Date(2024, 1, 1)); // Feb 1, 2024
                                                const maxDate = dayjs(new Date(2024, 6, 31)); // July 31, 2024
                                                if (!current) return false;
                                                return current.isBefore(minDate, 'day') || current.isAfter(maxDate, 'day');
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Chart Metric Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Chart Metric</span>
                                        <Select
                                            id='ChartMetricBirdsId'
                                            placeholder='Select a metric'
                                            value={chartDataKey}
                                            onChange={handleChartDataChange}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="uniqueSpecies">Number of Detections</Option>
                                            <Option value="count">Number of Species</Option>
                                        </Select>
                                    </div>
                                </div>

                                {/* Clear All Button */}
                                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                                    <Button
                                        onClick={clearFilters}
                                        type='link'
                                        size='large'
                                        style={{ height: '32px', color: '#2563eb', fontSize: '1rem' }}
                                    >
                                        Clear All
                                    </Button>
                                </div>

                                {/* Export to CSV Button */}
                                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                                    <Button
                                        onClick={exportToCsv}
                                        type='primary'
                                        icon={<DownloadOutlined />}
                                        size='large'
                                        style={{
                                            height: '32px',
                                            backgroundColor: '#329619', // The new background color
                                            borderColor: '#329619',     // Optional: Keep border color consistent
                                        }}
                                    >
                                        Export to CSV
                                    </Button>
                                </div>
                            </div>

                            <Table
                                id='TableBirdsId'
                                columns={columns}
                                dataSource={filteredData}
                                rowKey='key'
                                loading={loading}
                                pagination={{
                                    current: pagination.current,
                                    pageSize: pagination.pageSize,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                                    onChange: (page, pageSize) => {
                                        setPagination({ current: page, pageSize });
                                    },
                                }}
                                rowClassName={(record) => (selectedSensorId === record.sensorId ? 'bg-green-50' : '')}
                                onRow={(record) => ({
                                    onMouseEnter: () => setHoveredSensorId(record.sensorId),
                                    onMouseLeave: () => setHoveredSensorId(null),
                                    onClick: () => handleRowClick(record),
                                    style: {
                                        cursor: 'pointer',
                                        backgroundColor:
                                            selectedSensorId === record.sensorId
                                                ? '#f6ffed'
                                                : hoveredSensorId === record.sensorId
                                                    ? '#f6ffed'
                                                    : 'transparent',
                                    },
                                })}
                                style={{ borderRadius: '0.5rem', overflow: 'hidden' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Bar Chart Section */}
                <div style={{ paddingLeft: '8px', paddingRight: '8px', marginBottom: '1rem', flexBasis: '100%', maxWidth: '100%' }}>
                    <div style={{ marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                <span>
                                    {chartDataKey === 'count'
                                        ? 'Detections by Sensor/Mic'
                                        : 'Species Distribution by Sensor/Mic'
                                    }
                                </span>
                            </div>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            {(micChartData ?? []).length > 0 ? (
                                <div style={{ flexBasis: '100%', maxWidth: '100%', padding: '1rem', backgroundColor: '#fff', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                                        <Button
                                            onClick={downloadChart}
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            style={{ height: '32px', backgroundColor: '#329619', borderColor: '#329619' }}
                                        >
                                            Download Chart
                                        </Button>
                                    </div>
                                    <ResponsiveContainer width="100%" height={700} ref={chartRef}>
                                        <BarChart
                                            data={micChartData}
                                            margin={{
                                                top: 45,
                                                left: 20,
                                                bottom: 60,
                                            }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="sensorId"
                                                angle={-45}
                                                textAnchor="end"
                                                height={80}
                                                label={{ value: 'Sensor ID', offset: 0, position: 'insideBottom' }}
                                            />
                                            <YAxis
                                                label={chartDataKey === 'uniqueSpecies'
                                                    ? {
                                                        value: 'Number of Detections',
                                                        angle: -90,
                                                        offset: -5,
                                                        position: 'insideLeft',
                                                        bottom: 30,
                                                        top: 20,
                                                        left: 20,
                                                    }
                                                    : {
                                                        value: 'Number of Species',
                                                        angle: -90,
                                                        offset: -5,
                                                        position: 'insideLeft',
                                                        bottom: 30,
                                                        top: 20,
                                                        left: 20,
                                                    }
                                                }
                                            />
                                            <Tooltip
                                                formatter={(value, name) => [value, name]}
                                                labelFormatter={(label) => `Sensor: ${label}`}
                                            />
                                            {chartDataKey === 'count' ? (
                                                <Legend
                                                    content={() => (
                                                        <ul style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                                                            {Object.entries(areaColors).map(([area, color]) => (
                                                                <li key={area} style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                                                                    <div style={{ width: '10px', height: '10px', backgroundColor: color, marginRight: '5px' }}></div>
                                                                    <span style={{ fontSize: '14px', color: '#666' }}>{area.replace(/_/g, ' ')}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                />
                                            ) : (
                                                <Legend />
                                            )}
                                            {chartDataKey === 'count' ? (
                                                <Bar
                                                    dataKey="numberOfSpecies"
                                                    name="Number of Different Species"
                                                >
                                                    {(micChartData as { controlOrStudyarea: string }[]).map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={areaColors[entry.controlOrStudyarea]}
                                                        />
                                                    ))}
                                                </Bar>
                                            ) : (
                                                selectedSpecies.length > 0
                                                    ? selectedSpecies.map((speciesName) => (
                                                        <Bar
                                                            key={speciesName}
                                                            dataKey={speciesName}
                                                            stackId="species"
                                                            fill={speciesColors[speciesName]}
                                                            name={speciesName}
                                                        />
                                                    ))
                                                    : Object.keys(speciesColors).map((speciesName) => (
                                                        <Bar
                                                            key={speciesName}
                                                            dataKey={speciesName}
                                                            stackId="species"
                                                            fill={speciesColors[speciesName]}
                                                            name={speciesName}
                                                        />
                                                    ))
                                            )}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <span style={{ color: '#9ca3af' }}>
                                    No data available for chart. Apply filters or load data.
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export { SpeciesBirdGreekSpatialAnalysis };